import React, { useRef, useEffect } from 'react';
import './Background.css';

const Background = ({ color, type }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (type !== "brain") return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let nodes = [];
    let angle = 0;
    let time = 0;

    const NODE_COUNT = 800;
    const CONNECTION_LIMIT = 4;
    const ROTATION_SPEED = 0.015;
    const SIGNAL_SPEED = 3.5;
    const PULSE_FREQ = 0.08;
    const LINE_WIDTH_BASE = 6;
    const DOT_RADIUS_BASE = 1.6;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
    };

    const init = () => {
      resize();
      nodes = [];
      
      const isMobile = window.innerWidth <= 768;
      const scaleFactor = isMobile ? 0.45 : 0.65;
      
      for (let i = 0; i < NODE_COUNT; i++) {
        const theta = Math.random() * Math.PI * 2;
        const phi = Math.random() * Math.PI;
        
        const rx = 280 * scaleFactor, ry = 150 * scaleFactor, rz = 150 * scaleFactor;
        const folds = 1 + Math.sin(phi * 10) * Math.cos(theta * 8) * 0.12 + Math.sin(phi * 30) * 0.03;
        const fissure = Math.abs(Math.cos(theta * 2.5)) < 0.09 ? 0.7 : 1;
        
        const x = rx * folds * fissure * Math.sin(phi) * Math.cos(theta);
        const y = ry * folds * Math.cos(phi);
        const z = rz * folds * Math.sin(phi) * Math.sin(theta);
        
        nodes.push({ x, y, z, offset: Math.random() * Math.PI * 2, neighbors: [] });
      }

      nodes.forEach((node, i) => {
        const distances = nodes.map((n, j) => ({
          index: j,
          d: Math.pow(node.x - n.x, 2) + Math.pow(node.y - n.y, 2) + Math.pow(node.z - n.z, 2)
        })).filter(d => d.index !== i).sort((a, b) => a.d - b.d);
        node.neighbors = distances.slice(0, CONNECTION_LIMIT).map(d => d.index);
      });
    };

    const draw = () => {
      const rect = canvas.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;
      if (w === 0 || h === 0) return; 

      ctx.clearRect(0, 0, w, h);

      angle += ROTATION_SPEED;
      time += PULSE_FREQ;

      const isMobile = window.innerWidth <= 768;
      const centerX = w / 2;
      const centerY = h / 2;
      const breathe = 1 + Math.sin(time * 0.4) * 0.02;

      const projected = nodes.map(n => {
        const cosA = Math.cos(angle), sinA = Math.sin(angle);
        const rx = (n.x * breathe) * cosA - (n.z * breathe) * sinA;
        const rz = (n.x * breathe) * sinA + (n.z * breathe) * cosA;
        
        const perspective = isMobile ? 600 : 800;
        const p = perspective / Math.max(1, perspective + rz);
        
        return { 
          x: rx * p + centerX, 
          y: (n.y * breathe) * p + centerY, 
          z: rz, 
          pulse: Math.max(0, Math.sin(time * 1.5 + n.offset)), 
          neighbors: n.neighbors 
        };
      });

      projected.sort((a, b) => a.z - b.z).forEach(a => {
        const depth = Math.max(0, (a.z + 150) / 300);
        
        a.neighbors.forEach(nIdx => {
          const b = projected[nIdx];
          if (!b || b.z > a.z + 80) return;
          const dist = Math.hypot(a.x - b.x, a.y - b.y);
          const opacity = Math.max(0, (1 - dist / (isMobile ? 45 : 75)) * depth);

          ctx.beginPath();
          ctx.lineWidth = LINE_WIDTH_BASE * 0.25 * depth;
          ctx.strokeStyle = `rgba(0, 0, 0, ${opacity * 0.3})`;
          ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();

          if (a.pulse > 0.3) {
            const prog = (time * SIGNAL_SPEED + a.pulse) % 1.2;
            if (prog < 1) {
              ctx.beginPath();
              ctx.lineWidth = LINE_WIDTH_BASE * 0.6 * depth;
              const grad = ctx.createLinearGradient(a.x, a.y, b.x, b.y);
              grad.addColorStop(0, color); grad.addColorStop(1, "transparent");
              ctx.strokeStyle = grad; ctx.globalAlpha = opacity * a.pulse;
              ctx.moveTo(a.x, a.y);
              ctx.lineTo(a.x + (b.x - a.x) * prog, a.y + (b.y - a.y) * prog);
              ctx.stroke(); ctx.globalAlpha = 1;
            }
          }
        });

        const radius = DOT_RADIUS_BASE * depth;
        ctx.beginPath(); ctx.arc(a.x, a.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 0, 0, ${depth * 2})`; ctx.fill();

        if (a.pulse > 0.7) {
          ctx.beginPath(); ctx.arc(a.x, a.y, radius * 3, 0, Math.PI * 2);
          ctx.fillStyle = color; ctx.globalAlpha = (a.pulse - 0.7) * depth * 0.4;
          ctx.fill(); ctx.globalAlpha = 1;
        }
      });
      animationFrameId = requestAnimationFrame(draw);
    };

    init(); draw();
    const handleResize = () => { init(); resize(); };
    window.addEventListener('resize', handleResize);
    return () => { 
      cancelAnimationFrame(animationFrameId); 
      window.removeEventListener('resize', handleResize); 
    };
  }, [color, type]);

  return (
    <div className="brain-canvas-container">
      <canvas ref={canvasRef} className="brain-canvas-actual" />
    </div>
  );
};

export default Background;